import {ApiError, ICartPosition, ICartMessage, ICountriesLookup} from "../../../typescript-library";
import * as Sentry from "@sentry/browser";
import React, {lazy} from "react";
import {useTranslation} from "react-i18next";
import MediaQuery from "react-responsive";
import Checkout, {DEFAULT_ERROR_MESSAGE} from "../../checkout/Checkout";
import CheckoutContextProvider from "../../checkout/CheckoutContextProvider/CheckoutContextProvider";
import CheckoutViewContextProvider from "../../checkout/CheckoutContextProvider/CheckoutViewContextProvider";
import AdvantagesBannerMobile from "../../lib/Banner/AdvantagesBannerMobile";
import AdvantagesBannerDesktop from "../../lib/Banner/AdvantagesBannerDesktop";
import {PaymentMethodsBanner} from "../../lib/Banner/PaymentMethodsBanner";
import {Button, ButtonStyles, ButtonType} from "../../lib/form/Button/Button";
import BraintreeButton from "../../lib/payments/paypal/BraintreeButton";
import {useCartContext} from "../CartContextProvider/CartContextProvider";
import CartDetails from "../CartDetails/CartDetails";
import ItemListCart from "../ItemList/ItemList";
import PromotionalItems from "../ItemList/PromotionalItems";
import VoucherBlock from "../Rebates/VoucherBlock";
import {ShippingCostBar} from "../ShippingCostBar/ShippingCostBar";
import EmptyCartScreen from "./EmptyCart";
import styles from "./ShoppingCart.module.scss";
import "../../lib/ArrowBorder.scss";
import {updateUrlParams} from "../CartIcon/CartIconPortal";
import RedBorderBox from "../RedBorderBox";
import ErrorBoundary from "../../utils/ErrorBoundary";
import {Suspense} from "react";
import GPayButton from "../../lib/payments/googlepay/GPayButton";
import ApplePayButton from "../../lib/payments/applepay/ApplePayButton";
import {expressPaymentsError, INVALID_VAT_NUMBER} from "../../../helpers/errorHandling";
import {ButtonPropType} from "../../lib/payments/paypal/helpers/paypalTypes";

const RecommendationsCart = lazy(() => import("../../organisms/crossSelling/RecommendationsCart"));

const ShoppingCartComponent: React.FC = () => {
  const {cart, cartErrors, isIntoCheckout, setIsIntoCheckout, setCartErrors} = useCartContext();
  const {t} = useTranslation(["errorcodes", "translations"]);

  const itemPositions = cart.positions.filter((pos: ICartPosition) => pos.reference && pos.reference.itemId !== null);

  const invalidPositions = cart.positions.filter((pos) => !!pos.messages.find((msg) => msg.type === "error"));
  if (!!invalidPositions.length) {
    Sentry.setExtra("positions_with_error_messages", {
      deliveryCountry: cart.countryCode,
      errorPositions: invalidPositions.map((pos) => pos.messages.map((msg) => msg.code).join(" ")),
    });
  }

  const expressCheckoutError = (
    expressError: ApiError | PossibleNetworkError,
    occurrence?: string,
    additional?: Record<string, unknown>,
  ) => {
    const {errorArray, sentryEventId} = expressPaymentsError(expressError, occurrence, additional);

    setCartErrors(
      errorArray.map((err) => ({
        message: t(err, t(DEFAULT_ERROR_MESSAGE, {sentryEventId})),
        blockCheckout: false,
      })),
    );
  };

  const buttonGroup = React.useMemo(() => {
    // If any errors, show a disabled 'To checkout' button
    const isCartBlocked =
      cartErrors.some((cartError) => cartError.blockCheckout) ||
      (!cart.isValid && !cart.messages.some((msg) => msg.code === INVALID_VAT_NUMBER));

    const checkoutButtonReturn = () => {
      setIsIntoCheckout(true);
      updateUrlParams("checkout");
    };

    const params: ButtonPropType = {
      isBlocked: isCartBlocked,
      onReturn: checkoutButtonReturn,
      handleError: expressCheckoutError,
      place: "cart",
    };

    // Show internal and external checkout buttons
    return (
      <React.Fragment>
        <Button
          action={isCartBlocked ? undefined : checkoutButtonReturn}
          styles={[ButtonStyles.wide]}
          type={isCartBlocked ? ButtonType.disabled : ButtonType.default}
        >
          <i className={"icon icon-lock--white"} />
          {t("cart.toCheckout")}
        </Button>
        <div className={styles.separator}>
          <span>Express Checkouts</span>
        </div>
        <ApplePayButton {...params} />
        <BraintreeButton {...params} />
        <GPayButton {...params} />
      </React.Fragment>
    );
  }, [cartErrors]);

  if (!itemPositions.length) {
    return <EmptyCartScreen />;
  }

  if (isIntoCheckout) {
    return (
      <CheckoutContextProvider>
        <CheckoutViewContextProvider>
          <Checkout />
        </CheckoutViewContextProvider>
      </CheckoutContextProvider>
    );
  }

  return (
    <React.Fragment>
      <div className={styles.wrapper} role="main">
        <MediaQuery query="not all and (min-width: 768px)">
          <div>
            <ShippingCostBar />
            <h1>{t("cart.yourCart--count", {count: itemPositions.length})}</h1>
            <RedBorderBox />
            <PaymentMethodsBanner />
            <AdvantagesBannerMobile />
            <ItemListCart />
            <PromotionalItems />
            <VoucherBlock />
            <CountrySelector />
            <CartDetails />
            {buttonGroup}
          </div>
        </MediaQuery>
        <MediaQuery query="(min-width: 768px)">
          <div className={styles.list}>
            <h1>{t("cart.yourCart--count", {count: itemPositions.length})}</h1>
            <RedBorderBox />
            <ItemListCart />
            <PromotionalItems />
            <AdvantagesBannerDesktop />
          </div>
          <div className={styles.details}>
            <div className={styles.block}>
              <VoucherBlock />
              <CartDetails />
              <ShippingCostBar />
              <CountrySelector />
              <PaymentMethodsBanner />
              {buttonGroup}
            </div>
          </div>
        </MediaQuery>
      </div>
      <ErrorBoundary fallback={null} occurrence="BasketRecommSwiper">
        <div>
          <RecommendationsCart />
        </div>
      </ErrorBoundary>
    </React.Fragment>
  );
};

const CountrySelector = () => {
  const {cart, countries, setCartErrors} = useCartContext();
  const {i18n, t} = useTranslation(["errorcodes", "translations"]);
  const select = React.useRef<HTMLSelectElement>(null);

  const onSelect = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    e.persist();
    const countryCode = e.currentTarget.value;

    if (select.current) {
      const optionElement = select.current.querySelector(`[value=${countryCode}]`);

      if (optionElement) {
        optionElement.innerHTML = `${t("cart.shippingTo--country", {country: optionElement.innerHTML})}`;
      }
    }

    cart.put({countryCode}).catch((err: Error) => {
      const tempError = ((err as ApiError).originalError || err) as Error;
      tempError.name = "CountrySelect" + tempError.name;
      let sentryEventId = "";
      let defaultErrorMessage = "";
      if (!i18n.exists(tempError.message)) {
        sentryEventId = Sentry.captureException(tempError);
        defaultErrorMessage = t(DEFAULT_ERROR_MESSAGE, {ns: "errorcodes", sentryEventId});
      }
      setCartErrors([{message: defaultErrorMessage || t(tempError.message), blockCheckout: true}]);
    });
  };

  if (countries.length === 1) {
    return (
      <div style={{marginBottom: "1rem", padding: "0.5em 1em", border: "1px solid #f1f2f3"}}>
        {`${t("cart.shippingTo--country", {country: countries[0].name})}`}
      </div>
    );
  } else {
    return (
      <select
        value={cart.countryCode}
        ref={select}
        onChange={onSelect}
        style={{paddingLeft: "8px", width: "100%", marginBottom: "8px"}}
        aria-label={t("cart.shippingTo")}
      >
        {countries.length ? <CountryOptions countries={countries} /> : <option>{t("cart.shippingTo")}</option>}
      </select>
    );
  }
};

const CountryOptions: React.FC<{countries: ICountriesLookup[]}> = (props) => {
  const {cart} = useCartContext();
  const {t} = useTranslation(["errorcodes", "translations"]);

  return (
    <React.Fragment>
      {props.countries.map((country) => {
        return (
          <option key={country.countryCode} value={country.countryCode}>
            {cart.countryCode === country.countryCode
              ? `${t("cart.shippingTo--country", {country: country.name})} `
              : country.name}
          </option>
        );
      })}
    </React.Fragment>
  );
};

const ShoppingCart = () => {
  return (
    <ErrorBoundary fallback={null} occurrence="ShoppingCart">
      <Suspense fallback={null}>
        <ShoppingCartComponent />
      </Suspense>
    </ErrorBoundary>
  );
};

export default ShoppingCart;

export interface PossibleNetworkError extends Error {
  response?: {
    data: {
      messages?: ICartMessage[];
      result?: string & {code: string};
      detail?: string;
      status?: number;
      title?: string;
      type?: string;
    };
    status?: number;
  };
  code?: string;
}
